import AdminNav from '@/components/admin-nav';
import PaginatedNav from '@/components/pagination';
import { admin_user_membership_path, admin_user_path, admin_users_path } from '@/rails/routes';
import { formatShortDate } from '@/pages/reports/lib/format';
import { ROLE_OPTIONS, type TeamRole } from '@/pages/admin/users/lib/roles';
import type { AdminUser, AdminUsersIndex, WorkspacePageProps } from '@/types';
import { Avatar } from '@astryxdesign/core/Avatar';
import { Card } from '@astryxdesign/core/Card';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { HStack } from '@astryxdesign/core/HStack';
import { Icon } from '@astryxdesign/core/Icon';
import { Link } from '@astryxdesign/core/Link';
import { Selector } from '@astryxdesign/core/Selector';
import { Table, proportional, pixel } from '@astryxdesign/core/Table';
import type { TableColumn } from '@astryxdesign/core/Table';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { router, usePage } from '@inertiajs/react';
import { Users } from 'lucide-react';
import { useState } from 'react';

type Props = WorkspacePageProps & AdminUsersIndex;

type UserRow = AdminUser & Record<string, unknown>;

function displayDate(date: string | null | undefined): string {
  return date ? formatShortDate(date.slice(0, 10)) : 'Sin partes';
}

function RoleSelector({ user }: { user: AdminUser }) {
  const [isSaving, setIsSaving] = useState(false);

  const updateRole = (role: TeamRole) => {
    router.patch(
      admin_user_membership_path(user.id),
      { team_membership: { role } },
      {
        preserveScroll: true,
        onStart: () => setIsSaving(true),
        onFinish: () => setIsSaving(false),
      }
    );
  };

  return (
    <Selector
      label={`Rol de ${user.displayName}`}
      options={ROLE_OPTIONS}
      value={user.role}
      onChange={(role) => updateRole(role as TeamRole)}
      size="sm"
      isDisabled={isSaving || !user.roleChangeable}
      isLabelHidden
    />
  );
}

const columns: TableColumn<UserRow>[] = [
  {
    key: 'displayName',
    header: 'Usuario',
    width: proportional(3),
    renderCell: (user) => (
      <HStack gap={3} vAlign="center">
        <Avatar src={user.profilePhotoUrl} name={user.displayName} size="small" />
        <VStack gap={0.5}>
          <Link href={admin_user_path(user.id)} isStandalone>
            {user.displayName}
          </Link>
          <Text type="supporting" color="secondary" maxLines={1}>
            {user.email}
          </Text>
        </VStack>
      </HStack>
    ),
  },
  {
    key: 'role',
    header: 'Rol',
    width: pixel(160),
    renderCell: (user) => <RoleSelector user={user} />,
  },
  {
    key: 'reportsCount',
    header: 'Partes',
    width: pixel(100),
    align: 'end',
    renderCell: (user) => (
      <Text type="supporting" color="secondary" hasTabularNumbers>
        {user.reportsCount}
      </Text>
    ),
  },
  {
    key: 'lastReportDate',
    header: 'Último parte',
    width: pixel(140),
    align: 'end',
    renderCell: (user) => (
      <Text type="supporting" color="secondary" hasTabularNumbers>
        {displayDate(user.lastReportDate)}
      </Text>
    ),
  },
];

const Index = () => {
  const { pagination, users } = usePage<Props>().props;

  return (
    <VStack gap={6} width="100%" className="iorio-users-page">
      <AdminNav />

      <Card padding={6} className="iorio-users-card">
        <VStack gap={5}>
          <HStack hAlign="between" vAlign="start" gap={3} wrap="wrap">
            <VStack gap={1}>
              <HStack gap={2} vAlign="center">
                <span className="iorio-users-icon" aria-hidden="true">
                  <Icon icon={Users} size="sm" />
                </span>
                <Heading level={2}>Usuarios del equipo</Heading>
              </HStack>
              <Text type="supporting" color="secondary">
                La lista toma el equipo activo de tu sesión.
              </Text>
            </VStack>
            <Text type="supporting" color="secondary" hasTabularNumbers>
              {pagination.total} totales
            </Text>
          </HStack>

          {users.length === 0 ? (
            <section className="iorio-users-empty" aria-label="Estado vacío de usuarios">
              <EmptyState
                title="Todavía no hay usuarios"
                description="Cuando el equipo tenga integrantes, van a aparecer acá."
                icon={
                  <span className="iorio-users-icon" aria-hidden="true">
                    <Icon icon={Users} size="sm" />
                  </span>
                }
                isCompact
              />
            </section>
          ) : (
            <VStack gap={4} className="pb-2">
              <Table data={users as UserRow[]} columns={columns} idKey="id" hasHover />
              <HStack hAlign="center" width="100%">
                <PaginatedNav
                  pagination={pagination}
                  pathBuilder={({ page }) => admin_users_path({ page: page as number })}
                />
              </HStack>
            </VStack>
          )}
        </VStack>
      </Card>
    </VStack>
  );
};

export default Index;
